import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, CART_SESSION_STORAGE_KEY, clearCartSessionKey, ensureCartSessionKey } from '../api/client';
import type { Cart } from '../types/api';
import { useAuth } from './useAuth';

interface CartContextValue {
  cart: Cart | null;
  loading: boolean;
  addItem: (listingId: string, quantity: number, variantId?: string | null) => Promise<Cart | null>;
  updateQty: (itemId: string, quantity: number) => Promise<Cart | null>;
  removeItem: (itemId: string) => Promise<Cart | null>;
  refresh: () => Promise<Cart | null>;
}

const CartContext = createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: ReactNode }): JSX.Element {
  const { token, loading: authLoading } = useAuth();
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const prevTokenRef = useRef<string | null>(null);

  const fetchCart = useCallback(async (): Promise<Cart | null> => {
    setLoading(true);
    try {
      // For anonymous users, make sure a session key exists so the backend
      // can create/attach a cart. The axios interceptor sends it as a header.
      if (!token) ensureCartSessionKey();
      const resp = await api.get<Cart>('/api/cart');
      setCart(resp.data);
      return resp.data;
    } catch {
      setCart(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token]);

  // On mount + whenever token changes, refresh. On login (token went from null->set),
  // attempt merge of any anonymous session first.
  useEffect(() => {
    if (authLoading) return;
    const prev = prevTokenRef.current;
    prevTokenRef.current = token;

    (async () => {
      if (!prev && token) {
        // transition: logged out -> logged in; merge anonymous cart if present
        const session = typeof window !== 'undefined'
          ? window.localStorage.getItem(CART_SESSION_STORAGE_KEY)
          : null;
        if (session) {
          try {
            await api.post<Cart>('/api/cart/merge', { sessionKey: session });
          } catch {
            // non-fatal; we'll still refresh the cart below
          } finally {
            clearCartSessionKey();
          }
        }
      }
      await fetchCart();
    })();
  }, [token, authLoading, fetchCart]);

  const addItem = useCallback(async (
    listingId: string,
    quantity: number,
    variantId?: string | null,
  ): Promise<Cart | null> => {
    if (!token) ensureCartSessionKey();
    await api.post('/api/cart/items', {
      listingId,
      variantId: variantId ?? undefined,
      quantity,
    });
    return fetchCart();
  }, [token, fetchCart]);

  const updateQty = useCallback(async (itemId: string, quantity: number): Promise<Cart | null> => {
    await api.patch(`/api/cart/items/${itemId}`, { quantity });
    return fetchCart();
  }, [fetchCart]);

  const removeItem = useCallback(async (itemId: string): Promise<Cart | null> => {
    await api.delete(`/api/cart/items/${itemId}`);
    return fetchCart();
  }, [fetchCart]);

  const value = useMemo<CartContextValue>(() => ({
    cart,
    loading,
    addItem,
    updateQty,
    removeItem,
    refresh: fetchCart,
  }), [cart, loading, addItem, updateQty, removeItem, fetchCart]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
